import {AnyAttrs, AttrsSchema, Component} from 'panel';
import {VNode} from 'snabbdom';
import {produce} from 'immer';
import isNil from 'lodash/isNil';

import {Persistence} from 'common/report/persistence';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import type {ContextPanelAttrs, ContextPanelProps} from 'common/widgets/context-panel';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {emitTrackEvent, propagateEvent} from 'common/util/dom';
import {reporter} from 'common/report/error-reporting';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {LocalStorageKey, ProjectFeatureFlag} from 'common/report/constants';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {useStore} from 'common/util/component-helpers';
import {currentProjectStore} from 'common/report/stores/project-details';
import {ItemsMenu} from 'common/components/items-menu';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {featureSamplingUtils} from 'common/report/util/feature-sampling';

import {QueryContextRegistry} from './contexts/registry';
import {BETA_MENU_ROLLOUT_PERCENT, MetricType, TRACKING_EVENTS} from './constants';

export interface TooltipContextActionResult {
  handled: boolean;
  openedModal: boolean;
}

export interface BaseState {
  searchFilter?: string;
  staged?: any;
  selected?: any;
  keydownProxyEl?: HTMLElement;
  canUseBetaMenu?: boolean;
  isBetaVersionOn?: boolean;
  isModalOpen?: boolean;
}

export interface ChildMenuBaseAttrs<SelectedT = any> {
  'all-data-groups'?: boolean;
  'metric-type'?: MetricType;
  'data-group-id'?: string;
  'event-names'?: string[];
  'exclude-autotrack-events'?: boolean;
  'exclude-custom-events'?: boolean;
  'exclude-dimension-properties'?: boolean;
  'exclude-lookup-table-properties'?: boolean;
  'excluded-elements'?: any[];
  'max-data-history-days'?: number;
  multiple?: boolean;
  'project-id'?: number;
  'hide-events'?: boolean;
  'hide-all-properties-select'?: boolean;
  'project-has-people'?: boolean;
  'project-has-cohorts'?: boolean;
  readonly?: boolean;
  'show-special-date-property-unit'?: boolean;
  'show-special-dataset-property'?: boolean;
  open?: boolean;
  'search-filter'?: string;
  selected?: SelectedT;
  tabs?: any;
  'user-id'?: number;
  'inline-cohorts-override'?: {enable: boolean};
  'support-b2b-cohorts'?: boolean;
  'single-data-group'?: string;
  'hide-edit-controls'?: boolean;
  'workspace-id'?: number;
  'track-name'?: string;
  /** @deprecated DO NOT USE THIS, it will be removed eventually */
  sections?: any[];
}

export interface ContextPanelRendererParams {
  tooltipData: ContextPanelData;
  contextPanelAttrs?: ContextPanelAttrs;
  contextPanelProps?: ContextPanelProps;
  /** handles deleted entities from the context panel */
  handleSelect: (...args) => void;
  /** handles a deleted interaction from the context panel */
  handleDelete: (...args) => void;
  /** handles when the inner contents of the context panel are hovered in */
  handleHoverIn?: () => void;
  /** handles when the inner contents of the context panel are hovered out */
  handleHoverOut?: () => void;
  /** defines which tracking context the context panel should track to */
  track: (...args) => void;
  /** passes menu specific attributes for the context panel to render with */
  additionalArgs?: Obj;
  key?: string;
}

export type ContextPanelRenderer = (params: ContextPanelRendererParams) => VNode;

// Superclass for components rendered in mp-query-block's mp-drop-menu
export abstract class ChildMenu<StateT, AttrsT = AnyAttrs, SelectedT = any, ParamsT = any> extends Component<
  BaseState & StateT,
  ChildMenuBaseAttrs<SelectedT> & AttrsT,
  any,
  any,
  QueryContextRegistry,
  ParamsT
> {
  private _persistence: Persistence;
  protected itemsMenuEl: ItemsMenu;

  /** defines what context panel should render for a given menu type */
  static contextPanelRenderer: ContextPanelRenderer = null;
  private _userLocalStorageHelper: UserLocalStorageHelper<boolean>;
  private isBetaMenuStorageKey = `isBetaMenuVersionOn`;
  private _childMenuEl: HTMLElement;

  static get attrsSchema(): AttrsSchema<ChildMenuBaseAttrs> {
    return {
      ...super.attrsSchema,
      // crazy hack: Please DO NOT use `sections`, this is a temp workaround for signal to display context panel
      // TODO: Zihe remove this attr
      sections: {type: `json`, default: []},
      'all-data-groups': {
        type: `boolean`,
        description: `enables data groups property selection`,
      },
      'metric-type': {type: `string`, enum: Object.values(MetricType), default: ``},
      'data-group-id': {
        type: `string`,
        description: `defines the selected data group id`,
      },
      'event-names': {
        type: `json`,
        default: null,
        description: `when set, displayed properties belong only to the set of events passed`,
      },
      'exclude-autotrack-events': {
        type: `boolean`,
        description: `whether to hide autotrack events`,
      },
      'exclude-custom-events': {
        type: `boolean`,
        description: `wheter to hide custom events from event selection menus`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'exclude-lookup-table-properties': {
        type: `boolean`,
        description: `whether to hide lookup table properties from properties menu`,
      },
      'excluded-elements': {
        type: `json`,
        default: [],
        description: `defines items to be hidden from selection menus`,
      },
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      multiple: {
        type: `boolean`,
        description: `allows multi selection for selection menus`,
      },
      'project-id': `number`,
      'hide-events': {
        type: `boolean`,
        description: `when true, disables event property selection`,
      },
      'hide-all-properties-select': {
        type: `boolean`,
        description: `when true, does not show the "all properties" selection`,
      },
      'project-has-people': {
        type: `boolean`,
        description: `enables people properties selection`,
      },
      'project-has-cohorts': {
        type: `boolean`,
        description: `enables cohorts selection`,
      },
      readonly: `boolean`,
      'show-special-date-property-unit': {
        type: `boolean`,
        description: `disables the unit selection for special date property break-down`,
      },
      'show-special-dataset-property': {
        type: `boolean`,
        description: `enables segmentation and filtering by dataset for rollup projects`,
      },
      open: `boolean`,
      'search-filter': `string`,
      selected: {type: `json`, description: `selection object (varies by menu type)`},
      tabs: `json`,
      'user-id': `number`,
      'inline-cohorts-override': {
        type: `json`,
        description: `override cohorts access and allow inline cohorts`,
        default: null,
      },
      'support-b2b-cohorts': {
        type: `boolean`,
        description: `whether b2b cohorts are enabled`,
      },
      'single-data-group': {
        type: `string`,
        description: `if set, shows group properties for a specific data group id`,
      },
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'workspace-id': {
        type: `number`,
        description: `workspace to query for. if not provided, fall back to project queries`,
      },
      'track-name': {
        type: `string`,
        description: `If specified, includes "tab" as a property`,
      },
    };
  }

  get config() {
    return {
      template: null,
      helpers: {
        getSortByFields: () => this.getSortByFields(),
        propagateEvent: (ev) => propagateEvent(this, ev),
        customSearchMatcherFn: (item, searchString) => {
          return fuzzyMatch(item.searchableContent, searchString);
        },
        setChildMenuRef: (vnode: VNode) => {
          this._childMenuEl = vnode.elm as HTMLElement;
        },
        getChildMenuRef: () => this._childMenuEl,
      },
    };
  }

  get persistence() {
    return this._persistence;
  }

  connectedCallback() {
    this.createCache();
    super.connectedCallback();

    useStore(this, currentProjectStore).fetch();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    // prevent initialization race conditions by generating
    // a new cache whenever the namespace changes on menu open
    switch (attr) {
      case `project-id`: {
        const hasFeatureFlag = currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.NewDropdowns);
        const orgId = currentProjectStore.value?.organizationId;
        // TODO [Jordan] move eligibity check to currentProjectStore with an ignore list for org/project ids
        const isInRollout =
          orgId &&
          featureSamplingUtils.determineFeatureEligibility(
            orgId.toString(),
            ProjectFeatureFlag.NewDropdowns,
            BETA_MENU_ROLLOUT_PERCENT,
          );

        // @ts-ignore
        this.update({
          canUseBetaMenu: Boolean(hasFeatureFlag || isInRollout),
          isBetaVersionOn: null,
        });

        this.createCache();
        break;
      }
      case `user-id`:
      case `workspace-id`:
        this.createCache();
        break;
      case `open`:
        if (!this.attr(`open`)) {
          // @ts-ignore
          this.update({isBetaVersionOn: null}); // Reset cached menu version everytime we close the menu
        }

        break;
    }
  }

  public getSortByFields(): string[] {
    return [];
  }

  public createCache() {
    if (this.attr(`user-id`) && (this.sourceAttrs.projectId || this.sourceAttrs.workspaceId)) {
      this._persistence = Persistence.createCachedNamespace(
        // prefix the workspace so there are no collisions with project ids
        this.sourceAttrs.workspaceId ? `wksp-${this.sourceAttrs.workspaceId}` : this.sourceAttrs.projectId,
        this.attr(`user-id`),
        this.getPersistenceKey(),
      );
    }
  }

  public clearCache() {
    if (this._persistence) {
      this._persistence.clearCachedNamespace();
      this._persistence.clear(this.getPersistenceKey());
    }
  }

  // Keeps storage of items light
  public getMaxRecentItemsToPersist() {
    return 4;
  }

  public getPersistenceKey() {
    return `child-menu`;
  }

  public getItemUniqueKey(item) {
    return item.name;
  }

  public getRecentItems(subKey, limit = null) {
    if (this._persistence) {
      const items = this._persistence.getObject(`recent-items-${subKey}`) || [];

      return limit ? items.slice(0, limit) : items;
    }

    return [];
  }

  /**
   * Adds or moves the item to the top of the list -- effectively making this an LRU cache
   */
  public addRecentItem(subKey, item) {
    if (this._persistence) {
      let items = this.getRecentItems(subKey);
      items = items.filter((existingItem) => this.getItemUniqueKey(existingItem) !== this.getItemUniqueKey(item));
      items.unshift(item);
      items = items.slice(0, this.getMaxRecentItemsToPersist());
      this._persistence.setObject(`recent-items-${subKey}`, items);
    }
  }

  public getPrefixLabel() {
    return null;
  }

  public handleLabelClick() {
    return null;
  }

  public handleLabelMouseEnter() {
    return null;
  }

  public handleLabelMouseLeave() {
    return null;
  }

  /** defines if this menu is interactable */
  public canOpenMenu() {
    return !this.attrs() || !this.attr(`readonly`);
  }

  public shouldCloseOnSelect(_detail: any) {
    return true;
  }

  public selectedLabel(): string {
    return ``;
  }

  public selectedBrowserTooltipLabel(): string {
    return null;
  }

  public isUnsavedCustomEntity() {
    return false;
  }

  /** The icon that should appear to the left of the selected option */
  public selectedLeftIcon(): string {
    return ``;
  }

  /** The icon that should appear to the right of the selected option */
  public selectedRightIcon(): string {
    return ``;
  }

  public transformSelectedVal(val) {
    return val;
  }

  public handleCloseMenu() {
    return;
  }

  public getSelections(): string[] {
    return [];
  }

  public getSelectedProperty() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged ? this.state.staged.property : this.attr(`selected`);
    if (selected && selected.property) {
      selected = selected.property;
    }
    return selected;
  }

  private betaMenuProperties() {
    return {
      'menu mode': this.state.isBetaVersionOn ? `beta` : `classic`,
      'has access to beta menu': Boolean(this.state.canUseBetaMenu),
      'beta menu rollout percentage': BETA_MENU_ROLLOUT_PERCENT,
    };
  }

  public trackSelection({props, ...eventData}, cancelable = true) {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
    };
    if (this.attr(`track-name`)) {
      additionalProps[`tab`] = this.attr(`track-name`);
    }
    emitTrackEvent(this, {
      ...eventData,
      props: {
        ...props,
        ...this.betaMenuProperties(),
        ...additionalProps,
      },
      cancelable,
    });
  }

  public trackEvent(eventName, props = {}, {cancelable = false} = {}) {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
    };
    if (this.attr(`track-name`)) {
      additionalProps[`tab`] = this.attr(`track-name`);
    }
    emitTrackEvent(this, {
      props: {
        ...props,
        ...this.betaMenuProperties(),
        ...additionalProps,
      },
      eventName,
      cancelable,
    });
  }

  // TODO: "project-id" is type number while getProjectId() is type string. Should make these same type.
  // Same applies for workspace id
  get sourceAttrs() {
    return {
      projectId: this.attr(`project-id`) ?? getProjectId(),
      workspaceId: this.attr(`workspace-id`) ?? getWorkspaceId(),
    };
  }

  set keydownProxyEl(keydownProxyEl) {
    this.update({keydownProxyEl} as Partial<BaseState & StateT>);
  }

  protected retrieveDataGroups(): Promise<DataGroup[]> {
    const params = this.sourceAttrs;
    return dataGroupsClient.query(params).catch((error) => {
      reporter.error(`Error loading data groups`, error);
      return [];
    });
  }

  // Get tooltip data for the `selected` item
  public getSelectedHoverTooltipData(_ = {}): ContextPanelData {
    return null;
  }

  public getSelectedTrackingProps(): Obj {
    return null;
  }

  // Handler for context panel action. Returns boolean indicating if the action was handled.
  public handleTooltipContextAction(_ev: Event): TooltipContextActionResult {
    return null;
  }

  public produceUpdate(producer: (state: StateT) => void): void {
    this.update(produce(this.state, producer));
  }

  private get userLocalStorageHelper() {
    // @ts-ignore
    if (this.config?.contexts?.includes(`sessionStorageManager`) && this.attr(`user-id`)) {
      this._userLocalStorageHelper =
        this._userLocalStorageHelper ||
        new UserLocalStorageHelper({
          context: this.getContext(`sessionStorageManager`),
          namespace: LocalStorageKey.QueryBlockMenuVersion,
        });
    }
    return this._userLocalStorageHelper;
  }

  public setBetaVersion({isOn}: {isOn: boolean}): void {
    // @ts-ignore
    this.update({isBetaVersionOn: isOn});
    this.trackEvent(isOn ? TRACKING_EVENTS.BETA_MENU_TOGGLE_ON : TRACKING_EVENTS.BETA_MENU_TOGGLE_OFF, {
      'mode changed to': isOn ? `New` : `Classic`,
    });
    this.userLocalStorageHelper?.setItem({
      key: this.isBetaMenuStorageKey,
      userId: this.attr(`user-id`),
      value: isOn,
    });
  }

  /*
    isBetaMenu is a stateProxy that lazily syncs with localSessionStorage.
    Do not use update calls inside this methods as it is treated as state and referenced in templates.
  */
  public isBetaMenu(): boolean {
    if (isNil(this.state.isBetaVersionOn) && this.state.canUseBetaMenu) {
      const isOn = this.userLocalStorageHelper?.getItem({
        key: this.isBetaMenuStorageKey,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      });
      this.state.isBetaVersionOn = isOn ?? true;
      if (isNil(isOn)) {
        this.trackEvent(TRACKING_EVENTS.BETA_MENU_START_SESSION);
      }
      this.userLocalStorageHelper?.setItem({
        key: this.isBetaMenuStorageKey,
        userId: this.attr(`user-id`),
        value: isOn,
      });
    }
    return Boolean(this.state.isBetaVersionOn);
  }

  /**
   * Modal helpers: to be called by a class that implements ChildMenu when it opens a modal via promisifyModal.
   * Modal status needs to be maintained by the wrapping component QueryBlock in order to ensure
   * that the ChildMenu remains open (connected to DOM) and can handle modal events.
   */
  public onModalOpen() {
    this.dispatchEvent(new CustomEvent(`openedModal`));
    this.update({isModalOpen: true} as Partial<BaseState & StateT>);
  }

  public onModalClose() {
    this.dispatchEvent(new CustomEvent(`closedModal`));
    this.update({isModalOpen: false} as Partial<BaseState & StateT>);
  }
}
